import { useState, useEffect, useRef, type RefObject } from 'react';

type UseStickyResult = {
  isSticky: boolean;
  headerRef: RefObject<HTMLDivElement>;
};

const SCROLLABLE_VALUES = ['auto', 'scroll'];

const isScrollable = (style: CSSStyleDeclaration): boolean =>
  SCROLLABLE_VALUES.includes(style.overflow) ||
  SCROLLABLE_VALUES.includes(style.overflowY);

export const useSticky = (): UseStickyResult => {
  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const rect = headerRef.current.getBoundingClientRect();
        setIsSticky(rect.top < 0);
      }
    };

    const scrollableElements: Element[] = [];

    document.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true
    });
    window.addEventListener('scroll', handleScroll, { passive: true });

    document.querySelectorAll('*').forEach(el => {
      if (isScrollable(window.getComputedStyle(el))) {
        el.addEventListener('scroll', handleScroll, { passive: true });
        scrollableElements.push(el);
      }
    });

    handleScroll();

    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('scroll', handleScroll);
      scrollableElements.forEach(el => {
        el.removeEventListener('scroll', handleScroll);
      });
    };
  }, []);

  return { isSticky, headerRef };
};
